/**
 * TRIM / EXTEND of a heavy `POLYLINE` as the TARGET (the object being cut or
 * grown) — @codex PR #785 round 4, P2.
 *
 * The GEOMETRY is not re-derived here. A heavy 2D polyline's drawn chain is
 * exactly the `{x, y, bulge}` chain `trimPolylineTarget` / `extendPolylineTarget`
 * already parameterise for a lightweight one, so this module is a PROJECTION:
 * `./heavyPolylineTrim.ts` (over `cad-core`'s `heavyPolylineRuns` topology)
 * flattens the record run into that chain, the proven LWPOLYLINE-target math
 * does the cutting, and this module writes the survivors back as VERTEX
 * RECORDS. Nothing about bulges, closed domains or span selection is
 * reimplemented — which is the point: a bulged span is cut on its ARC by
 * `subArcBulge`'s exact `tan(θ·Δt/4)`, never on the chord, because it is the
 * same code path a lightweight polyline has always taken.
 *
 * ## What changes on a heavy polyline that does not change on a light one
 *
 * 1. **`closed` is a BIT, not a boolean field.** A lightweight polyline carries
 *    `closed: boolean`; a heavy one carries DXF group 70 with bit 1
 *    ({@link DXF_POLYLINE_FLAG.CLOSED}). A trimmed CLOSED polyline becomes an
 *    OPEN one — AutoCAD parity, the same rule that turns a trimmed Circle into
 *    an Arc and that `trimPolylineTarget` already encodes as `closed: false` —
 *    so the survivor clears that bit rather than writing a field the entity
 *    does not have.
 * 2. **A vertex is a full DXF RECORD.** Its group-5 handle, `1001` XDATA,
 *    `102 {ACAD_XDICTIONARY` / `{ACAD_REACTORS` blocks, unmapped groups and
 *    non-canonical `100` extensions belong to that vertex alone and nothing
 *    can reconstruct them. Every SURVIVING vertex is therefore carried over
 *    VERBATIM through {@link PolylineVertexProvenance}, and only a freshly-cut
 *    point is a new record. Rebuilding all of them from the geometry would be
 *    the same per-VERTEX deletion PR #785 round 1 repaired one level up.
 * 3. **Width presence is information, but absence is not zero.** On a VERTEX,
 *    groups 40/41 OVERRIDE the owning POLYLINE's 40/41 defaults, so "absent"
 *    (inherit) and "present and 0" (force zero) are DIFFERENT — the dxf-io
 *    writer preserves that distinction deliberately. Two things follow, and
 *    BOTH are needed for a cut to taper correctly:
 *    - the taper math is fed the RESOLVED pair
 *      (`drawing-model`'s {@link heavyPolylineWidths}, the one truth for
 *      "record 40/41, else the POLYLINE's own 40/41"), never the raw absent
 *      field — reading an inherited `40 = 10` as `0` would cut a wide span to
 *      a zero-width endpoint;
 *    - the survivor may stay ABSENT only where inheriting still reproduces
 *      that resolved value. A span that was CUT tapers over a SHORTER run, so
 *      its endpoint widths are no longer the inherited pair and have to be
 *      stated; a span that survived whole still inherits exactly and must not
 *      be rewritten (see {@link rebuildVertexRecord}).
 *
 * Which subtypes are eligible at all, and why the others are REFUSED VISIBLY
 * instead of silently ignored, is documented in `./heavyPolylineTrim.ts`.
 */

import {
  DXF_POLYLINE_FLAG,
  heavyPolylineWidths,
  type PolylineEntity,
  type PolylineVertexRecord,
  type PolylineWidth,
} from "@dxf-ai-editor/drawing-model";

import {
  heavyPolylineChain,
  type HeavyPolylineChainVertex,
} from "./heavyPolylineTrim";
import {
  extendPolylineTarget,
  trimPolylineTarget,
  type PolyVertex,
  type PolylineTargetResult,
  type PolylineVertexProvenance,
} from "./trimExtendPolylineTarget";
import type { CarrierTaggedPoint, IntPoint } from "./trimExtendGeometryCore";

/**
 * Two widths are the SAME width, to the precision the taper interpolation can
 * actually deliver.
 *
 * An UNTOUCHED span is still re-interpolated, as `a + (b - a) * 1`, and that is
 * not bit-identical to `b` for every pair of doubles: the subtraction CANCELS
 * when the two differ in magnitude, so e.g. `a = 89565.44426138727`,
 * `b = 5637.58952481402` comes back as `5637.589524814015`. An exact `===` in
 * the inherit test below would therefore mint a spurious — and very slightly
 * WRONG — explicit `41` onto a record the trim never touched, which is exactly
 * the rewrite {@link rebuildVertexRecord}'s rule exists to avoid (that pair is
 * the fixture in "keeps a DRIFTING inherited width inheriting").
 *
 * The bound is RELATIVE (an authored width has no fixed scale) with an absolute
 * floor of one `WIDTH_EPS`, and sits three orders of magnitude BELOW the
 * module's own `GEO_EPS` length tolerance, so it can only ever absorb
 * double-rounding noise: a cut close enough to a vertex to move a width by less
 * than this produces a span whose taper is geometrically indistinguishable from
 * the inherited one anyway.
 */
const WIDTH_EPS = 1e-12;

function sameWidth(a: number, b: number): boolean {
  return Math.abs(a - b) <= WIDTH_EPS * Math.max(1, Math.abs(a), Math.abs(b));
}

/**
 * Rebuild ONE surviving VERTEX record.
 *
 * `source` is the record this output vertex IS (provenance `sourceIndex`), or
 * `null` for a freshly-cut point; `segmentStart` is the record owning the span
 * the point sits on, which is where DXF puts the bulge and the width taper of
 * that span (Autodesk DXF reference, VERTEX groups 40/41/42) and therefore the
 * only record whose channels a fresh point may legitimately inherit.
 * `inherited` is what an ABSENT 40/41 resolves to on this entity (see
 * {@link inheritedWidth}), and decides whether this record can afford to stay
 * absent at all.
 */
function rebuildVertexRecord(
  out: PolyVertex,
  source: PolylineVertexRecord | null,
  segmentStart: PolylineVertexRecord,
  inherited: PolylineWidth,
): PolylineVertexRecord {
  const bulge = out.bulge ?? 0;
  const startWidth = out.startWidth ?? 0;
  const endWidth = out.endWidth ?? 0;
  // The widths the trim computed are the RESOLVED ones (`toPolyVertex` feeds
  // the taper math `heavyPolylineWidths`, never the raw absent field), so they
  // are directly comparable with what an absent group would inherit. Equal ⇒
  // this vertex can still inherit EXACTLY and the group stays absent; unequal ⇒
  // the span was shortened and the taper must be stated, or the survivor would
  // silently re-apply the FULL default taper over a shorter run.
  const inheritsStart = sameWidth(startWidth, inherited.start);
  const inheritsEnd = sameWidth(endWidth, inherited.end);

  if (source) {
    // An UNTOUCHED record: copy every channel that is NOT geometry, then state
    // the three geometry groups from scratch. Stating them beats patching a
    // spread: "write it only when it changed" would guard an IDEMPOTENT write
    // (the spread already carries the old value), so the guard could only ever
    // be observed through group PRESENCE — which the two rules below decide
    // directly and unambiguously instead.
    //
    // `location.z` rides along untouched: on a 2D polyline the per-vertex group
    // 30 is an OCS duplicate of the entity's own elevation plane, not
    // independent geometry, so re-deriving it would only risk rewriting what
    // the producer wrote.
    const { bulge: _b, startWidth: _sw, endWidth: _ew, ...carried } = source;
    const record: PolylineVertexRecord = {
      ...carried,
      location: { ...source.location, x: out.x, y: out.y },
    };
    // Group 42: absent and 0 mean the SAME thing (a straight span — Autodesk
    // DXF reference, VERTEX group 42; the dxf-io writer skips an explicit
    // `42 0` for exactly that reason), so the minimal record carries the group
    // only for a genuinely curved span.
    if (bulge !== 0) record.bulge = bulge;
    // Groups 40/41 are the OPPOSITE: absent means "inherit the POLYLINE's own
    // 40/41 default", so a record that declared a width keeps declaring one
    // (at its recomputed taper value) and a record that inherited keeps
    // inheriting — UNLESS inheriting would now be wrong. Writing `40 0` onto an
    // inheriting record would FORCE zero width and visibly thin the line
    // wherever the parent default is non-zero; leaving an inheriting record
    // absent when the trim shortened ITS OWN span would re-apply the full
    // default taper (a `10 → 20` default over a half-length remainder instead
    // of the `10 → 15` the cut actually produced). Decided PER SIDE, because
    // the inheritance the resolver implements is per side — so a record that
    // authored only its `40` keeps authoring only its `40` unless its `41`
    // genuinely changed.
    if (source.startWidth !== undefined || !inheritsStart) {
      record.startWidth = startWidth;
    }
    if (source.endWidth !== undefined || !inheritsEnd) {
      record.endWidth = endWidth;
    }
    return record;
  }

  // A FRESH cut point. It exists in no source record, so it carries no handle
  // (the conformant export mints one for an empty handle, the same as for any
  // bridge-built vertex), no XDATA, no extension dictionary, no reactors and no
  // subclass extension — inheriting a neighbour's would ATTACH that neighbour's
  // third-party data to a point the file never had.
  const record: PolylineVertexRecord = {
    handle: "",
    // The plane's own Z (see above): copy the span's start record rather than
    // inventing a 0 the producer may not have written.
    location: { x: out.x, y: out.y, z: segmentStart.location.z ?? 0 },
  };
  if (bulge !== 0) record.bulge = bulge;
  // Widths exactly when inheriting would NOT reproduce them. A fresh point sits
  // PART WAY along its span, so it takes the default taper's middle value
  // (`10 → 20` cut at the middle gives it a `15`) and an absent group would hand
  // it the span start's `10` instead; a point on a span that declares its own
  // 40/41 is off the inherited pair for the same reason. The two cases need no
  // separate test — a span that DECLARES a width the entity default already
  // equals is, by construction, one this point can still inherit exactly, and
  // writing the groups anyway would only add bytes the file never had. (That
  // redundancy was measured, not assumed: an explicit `segmentStart.startWidth
  // !== undefined || segmentStart.endWidth !== undefined ||` clause here left
  // four mutants alive that no input could kill.)
  //
  // Written as the PAIR, unlike a surviving record's per-side rule above,
  // because a fresh point has no authored presence to preserve — the two groups
  // it gets describe the one span it just created.
  if (!inheritsStart || !inheritsEnd) {
    record.startWidth = startWidth;
    record.endWidth = endWidth;
  }
  return record;
}

/**
 * What an ABSENT group 40 / 41 resolves to on THIS entity.
 *
 * Read back out of `drawing-model`'s {@link heavyPolylineWidths} rather than
 * from `entity.defaultStartWidth ?? 0` directly: the inheritance rule — which
 * record field wins, and what a POLYLINE that declares no default of its own
 * falls back to — is that module's alone (it is mid-fix there for the
 * LWPOLYLINE group-43 channel), and a second copy here would be a second truth
 * that drifts silently. A single synthetic record declaring NEITHER group is by
 * definition resolved to exactly what inheriting yields.
 *
 * EQUIVALENT MUTANTS (targeted-cad-mutation-testing's equivalent-mutant
 * contract) on the synthetic record's line — `ObjectLiteral` (`{}` for the
 * record or for its `location`), `ArrayDeclaration` (`[]` for the one-record
 * list) and `StringLiteral` (any handle): the record is passed to a resolver
 * that reads ONE thing from it, the presence of groups 40/41, and the whole
 * point of this record is that it declares NEITHER. Its handle and location are
 * structural filler the `PolylineVertexRecord` type requires and nothing reads,
 * and an empty `vertices` list resolves index 0 to no record — which inherits
 * for the same reason an empty record does. The invariant is
 * `heavyPolylineWidths`' own contract (`v?.startWidth ?? fallbackStart`), pinned
 * by `drawing-model`'s `polylineWidth.test.ts`. The `[0]` index is NOT
 * suppressed: it names the one entry this call asks for.
 */
function inheritedWidth(entity: PolylineEntity): PolylineWidth {
  return heavyPolylineWidths(
    // Stryker disable next-line ObjectLiteral,ArrayDeclaration,StringLiteral: a record declaring NO width group is the whole input; its handle, location and even its existence are unread filler (see equivalent-mutant note above)
    { ...entity, vertices: [{ handle: "", location: { x: 0, y: 0, z: 0 } }] },
    [0],
  )[0]!;
}

/**
 * Map one `PolylineTargetResult` back onto `entity`, producing the survivor.
 *
 * `clearClosed` clears DXF group 70 bit 1 (TRIM always opens a closed
 * polyline); EXTEND leaves the flags alone because it only ever grows an
 * already-open chain's terminal vertex.
 */
function applyResultToEntity(
  entity: PolylineEntity,
  indices: readonly number[],
  result: PolylineTargetResult,
  clearClosed: boolean,
  inherited: PolylineWidth,
): PolylineEntity {
  // `provenance` is one entry per output vertex BY CONSTRUCTION
  // (`trimExtendPolylineTarget.ts` builds the two arrays in lockstep), and
  // every index it names is a real `indices` / `entity.vertices` slot — so no
  // fallback is written here: a defensive `?? vertices[0]` would be dead code
  // that can only mask a contract break instead of surfacing it.
  const vertices = result.vertices.map((out, k) => {
    const prov: PolylineVertexProvenance = result.provenance[k]!;
    // EQUIVALENT MUTANT (targeted-cad-mutation-testing's equivalent-mutant
    // contract), ConditionalExpression `false` (always take the index branch):
    // a FRESH vertex's `sourceIndex` is `null`, and JavaScript resolves
    // `indices[null]` to `undefined` and then `entity.vertices[undefined]` to
    // `undefined` — which `rebuildVertexRecord`'s own `if (source)` treats
    // exactly as the `null` this branch produces. The mutant reaches the same
    // fresh-record path by a longer route, so no input can observe it. The
    // `true` direction and the `=== null` -> `!== null` EqualityOperator are
    // NOT suppressed: both lose the surviving records' handles and ARE killed
    // (see "keeps every surviving VERTEX record verbatim").
    //
    // NOTE: the directive sits DIRECTLY above the mutated condition's OWN line,
    // not above the `const` — Stryker resolves "next-line" against the mutated
    // expression's `loc.start.line` (the trap `./trimExtendPolylineTarget.ts`
    // documents at its own `arc.signedSweep >= 0` site).
    const sourceRecord =
      // Stryker disable next-line ConditionalExpression: a null sourceIndex indexes to undefined, which rebuildVertexRecord treats identically to null (see equivalent-mutant note above)
      prov.sourceIndex === null
        ? null
        : entity.vertices[indices[prov.sourceIndex]!]!;
    const segmentStart = entity.vertices[indices[prov.segmentIndex]!]!;
    return rebuildVertexRecord(out, sourceRecord, segmentStart, inherited);
  });
  return {
    ...entity,
    flags: clearClosed
      ? entity.flags & ~DXF_POLYLINE_FLAG.CLOSED
      : entity.flags,
    vertices,
  };
}

/**
 * Strip the IDENTITY handles from a split-off fragment.
 *
 * A mid-chain TRIM yields TWO entities. The first keeps the target's id (and
 * therefore its handles); the second is a genuinely NEW object, and a heavy
 * POLYLINE owns CHILD records whose group-5 handles are what
 * `ACDBASSOCGEOMDEPENDENCY` and reactor records point at. Letting both
 * fragments claim the same VERTEX / SEQEND handles would make two objects
 * answer to one id — so the fragment's own identity handles are cleared and the
 * conformant export mints fresh ones. Everything that is DATA rather than
 * identity (XDATA, extension dictionaries, reactors, the SEQEND's carried
 * record) rides along, exactly as it does on the id-preserving half.
 */
function mintableFragment(entity: PolylineEntity): PolylineEntity {
  // The ENTITY's own group-5 handle is its `id` (`dxfBridge.conformantSkeleton
  // .handles.ts`: "the entity's own `id` reused as the `handle`"), and the
  // reducer already allocates a fresh id for a split fragment — so only the
  // CHILD record handles this entity owns, which nothing upstream re-mints,
  // have to be released here.
  const { seqendHandle: _seqendHandle, ...rest } = entity;
  return {
    ...rest,
    vertices: entity.vertices.map((v) => ({ ...v, handle: "" })),
  };
}

/**
 * TRIM a heavy POLYLINE target. Returns the surviving polylines (one for a
 * near-end trim, two for a mid-chain split), or `null` for a no-op — including
 * every subtype `heavyPolylineTrimRefusal` declines in the `"target"` role.
 *
 * The FIRST survivor is the one the caller keeps the target's id on; any
 * further fragment comes back with its identity handles cleared (see
 * {@link mintableFragment}).
 */
export function trimHeavyPolylineTarget(
  entity: PolylineEntity,
  cutPoints: IntPoint[],
  pickPoint: IntPoint,
): PolylineEntity[] | null {
  const input = heavyPolylineTargetInput(entity);
  if (!input) return null;
  const results = trimPolylineTarget(input.polyline, cutPoints, pickPoint);
  if (!results) return null;
  return results.map((result, i) => {
    const survivor = applyResultToEntity(
      entity,
      input.indices,
      result,
      true,
      input.inherited,
    );
    return i === 0 ? survivor : mintableFragment(survivor);
  });
}

/**
 * EXTEND a heavy POLYLINE target: grow the end nearest the pick out to the
 * closest boundary crossing beyond it. Returns `null` for a no-op — a closed
 * polyline has no dangling end, and every declined subtype is `null` too.
 */
export function extendHeavyPolylineTarget(
  entity: PolylineEntity,
  boundaryPoints: CarrierTaggedPoint[],
  pickPoint: IntPoint,
): PolylineEntity | null {
  const input = heavyPolylineTargetInput(entity);
  if (!input) return null;
  const result = extendPolylineTarget(
    input.polyline,
    boundaryPoints,
    pickPoint,
  );
  if (!result) return null;
  // EQUIVALENT MUTANT (targeted-cad-mutation-testing's equivalent-mutant
  // contract), `false` -> `true` BooleanLiteral: `clearClosed` clears DXF group
  // 70 bit 1, and this call site can only ever be reached with that bit ALREADY
  // 0. `chain.closed` is `polylineIsClosed(entity)` (the same bit), and
  // `extendPolylineTarget`'s own first guard returns `null` for a closed chain
  // — so `result` is non-null only when the bit is clear, and `flags & ~CLOSED`
  // is then bit-identical to `flags`. Asserting the difference would require an
  // input that cannot exist. Proven by `extendHeavyPolylineTarget` declines a
  // CLOSED polyline in `./heavyPolylineTrimTarget.test.ts`.
  return applyResultToEntity(
    entity,
    input.indices,
    result,
    // Stryker disable next-line BooleanLiteral: EXTEND is unreachable for a closed chain, so clearing an already-clear group-70 bit 1 is a no-op (see equivalent-mutant note above)
    false,
    input.inherited,
  );
}

/**
 * The heavy polyline as the LWPOLYLINE-target math wants it: the drawn chain
 * (`./heavyPolylineTrim.ts`) with every span's widths RESOLVED, plus what the
 * write-back needs to decide presence again afterwards.
 *
 * Shared by both entry points rather than written twice — the resolution and
 * the write-back's `inherited` pair are ONE decision about this entity, and two
 * copies could resolve a chain one way and judge its absence by another.
 */
function heavyPolylineTargetInput(entity: PolylineEntity): {
  indices: readonly number[];
  polyline: { vertices: PolyVertex[]; closed: boolean };
  inherited: PolylineWidth;
} | null {
  const chain = heavyPolylineChain(entity, "target");
  if (!chain) return null;
  // `heavyPolylineWidths` answers per VISIBLE-run index, in run order, which is
  // exactly `chain.vertices`' own order (`chain.indices[k]` is the record
  // `chain.vertices[k]` came from), so the two zip positionally.
  const widths = heavyPolylineWidths(entity, chain.indices);
  return {
    indices: chain.indices,
    polyline: {
      vertices: chain.vertices.map((v, k) => toPolyVertex(v, widths[k]!)),
      closed: chain.closed,
    },
    inherited: inheritedWidth(entity),
  };
}

/**
 * The chain vertex as the LWPOLYLINE-target math wants it, carrying the span's
 * RESOLVED widths so the taper interpolation runs on the widths the file
 * actually draws rather than on an assumed zero.
 *
 * `width` comes from `drawing-model`'s `heavyPolylineWidths`, so a record that
 * declares NEITHER group arrives here as the POLYLINE's own 40/41 defaults
 * instead of as `undefined`. Feeding the raw record fields instead (what this
 * did before @codex round 5) made the taper math read every inherited width as
 * `0`: a cut through a span inheriting a `10 → 20` default produced a
 * zero-width endpoint, and the survivor then kept inheriting the FULL default
 * taper over a shortened run. Presence — "did the RECORD declare a width?" — is
 * a different question, answered from the record itself in
 * {@link rebuildVertexRecord}, which is the only place it is observable.
 */
function toPolyVertex(
  v: HeavyPolylineChainVertex,
  width: PolylineWidth,
): PolyVertex {
  return {
    x: v.x,
    y: v.y,
    bulge: v.bulge,
    startWidth: width.start,
    endWidth: width.end,
  };
}
